import { useState, type Dispatch, type SetStateAction } from "react";
import { openExternalAppLink } from "./api";
import type { AppSnapshot, Plan, Screen } from "./types";
import {
  ActionRow,
  EmptyCard,
  Icon,
  InfoRow,
  Intro,
  MenuRow,
  Notice,
  PrimaryButton,
  SecondaryButton,
} from "./ui";

type Go = (screen: Screen) => void;

export function HomeScreen({
  data,
  go,
}: {
  data: AppSnapshot;
  go: Go;
}) {
  if (data.vpn.state === "never_had") {
    return (
      <>
        <Intro title="VPN не подключён" subtitle="Оформите подписку, чтобы начать" />
        <div className="block-gap-28" />
        <EmptyCard
          icon="lock"
          title="У вас пока нет VPN"
          text={<>После покупки здесь появится<br />ваша активная подписка.</>}
        />
        <div className="block-gap-24" />
        <PrimaryButton icon="card" onClick={() => go("buy")}>Купить VPN</PrimaryButton>
        <div className="dual-actions">
          <SecondaryButton icon="book" onClick={() => go("instruction")}>Инструкция</SecondaryButton>
          <SecondaryButton icon="chat" onClick={() => go("support")}>Поддержка</SecondaryButton>
        </div>
      </>
    );
  }

  if (data.vpn.state === "expired") {
    return (
      <>
        <Intro title="VPN истёк" subtitle="Продлите подписку, чтобы снова подключиться" />
        <div className="block-gap-24" />
        <Notice
          tone="warning"
          icon="shield"
          title="Статус: истёк"
          text="VPN и старая ссылка сохранены"
        />
        <div className="block-gap-12" />
        <Notice
          icon="info"
          title="Последний тариф"
          text={[data.vpn.lastPlanLabel, ...data.vpn.regions].filter(Boolean).join(" · ") || "Тариф не указан"}
        />
        <div className="block-gap-20" />
        <PrimaryButton icon="refresh" onClick={() => go("renew")}>Продлить VPN</PrimaryButton>
        <div className="block-gap-14" />
        <Notice
          icon="key"
          title="Ссылка не изменится"
          text="После продления продолжите использовать ту же ссылку"
        />
      </>
    );
  }

  return (
    <>
      <Intro title="VPN активен" subtitle="Подключение готово к использованию" />
      <section className="status-card home-card">
        <InfoRow icon="shield" label="Статус">
          <span className="status-pill">Активен</span>
        </InfoRow>
        <InfoRow icon="calendar" label="Действует до">
          {data.vpn.expiresAt}
        </InfoRow>
        <InfoRow icon="globe" label="Регионы">
          <span className="regions">{data.vpn.regions.join(" + ") || "—"}</span>
        </InfoRow>
        <InfoRow icon="devices" label="Устройства" last>
          до {data.vpn.deviceLimit}
        </InfoRow>
      </section>

      <div className="home-cta">
        <PrimaryButton onClick={() => go("vpn")}>Открыть мой VPN</PrimaryButton>
      </div>

      <div className="quick-grid">
        <button className="quick-card pressable" onClick={() => go("renew")}>
          <span className="quick-icon"><Icon name="refresh" size={18} /></span>
          <strong>Продлить</strong>
          <small>Продлить этот VPN</small>
        </button>
        <button className="quick-card pressable" onClick={() => go("add-devices")}>
          <span className="quick-icon"><Icon name="plus" size={18} /></span>
          <strong>Добавить</strong>
          <small>Ещё одно устройство</small>
        </button>
      </div>
    </>
  );
}

export function VpnScreen({
  data,
  go,
  toast,
}: {
  data: AppSnapshot;
  go: Go;
  toast: (message: string) => void;
}) {
  if (data.vpn.state === "never_had") {
    return (
      <>
        <Intro title="Мой VPN" subtitle="У вас ещё нет VPN" />
        <div className="block-gap-26" />
        <EmptyCard
          icon="lock"
          title="Создайте свой VPN"
          text={<>После покупки здесь появятся<br />ссылка, устройства и управление.</>}
        />
        <div className="block-gap-24" />
        <PrimaryButton icon="card" onClick={() => go("buy")}>Купить VPN</PrimaryButton>
      </>
    );
  }

  if (data.vpn.state === "expired") {
    const copyExpired = async () => {
      if (!data.vpn.subscriptionUrl) return;
      await navigator.clipboard.writeText(data.vpn.subscriptionUrl);
      toast("Ссылка скопирована");
    };

    return (
      <>
        <Intro title="Мой VPN" subtitle="Подписка закончилась" />
        <div className="block-gap-26" />
        <EmptyCard
          icon="shield"
          tone="warning"
          title="VPN временно неактивен"
          text={<>Продлите подписку — ваша прежняя ссылка<br />и настройки подключения сохранятся.</>}
        />
        <div className="block-gap-24" />
        <PrimaryButton icon="refresh" onClick={() => go("renew")}>Продлить VPN</PrimaryButton>
        <div className="block-gap-18" />
        <ActionRow
          icon="key"
          title="Скопировать старую ссылку"
          subtitle="Она сохранена за аккаунтом"
          onClick={copyExpired}
        />
      </>
    );
  }

  const copyLink = async () => {
    if (!data.vpn.subscriptionUrl) return;
    await navigator.clipboard.writeText(data.vpn.subscriptionUrl);
    toast("Ссылка скопирована");
  };

  const openHapp = async () => {
    if (!data.vpn.subscriptionUrl) return;

    try {
      await navigator.clipboard.writeText(data.vpn.subscriptionUrl);
    } catch {
      // Clipboard is only a fallback; continue to the external launcher page.
    }

    const target = "happ://add/" + data.vpn.subscriptionUrl;
    const launcher = new URL("/happ-open.html", window.location.origin);
    launcher.searchParams.set("target", target);
    launcher.searchParams.set("subscription", data.vpn.subscriptionUrl);

    toast("Ссылка скопирована. Открываем Happ…");
    openExternalAppLink(launcher.toString());
  };

  return (
    <>
      <Intro title="Мой VPN" subtitle="Подключение и управление" />
      <section className="status-card vpn-status-card">
        <InfoRow icon="shield" label="Статус">
          <span className="status-pill">Активен</span>
        </InfoRow>
        <InfoRow icon="calendar" label="Действует до">
          {data.vpn.expiresAt}
        </InfoRow>
        <InfoRow icon="clock" label="Осталось" last>
          {data.vpn.daysLeft} дня
        </InfoRow>
      </section>

      <div className="vpn-primary">
        <PrimaryButton onClick={openHapp}>Открыть в Happ</PrimaryButton>
      </div>

      <button className="secondary-line pressable copy-line" onClick={copyLink}>
        <Icon name="key" size={20} />
        <strong>Скопировать ссылку VPN</strong>
        <span className="grow" />
        <Icon name="chevron" size={18} />
      </button>

      <div className="vpn-actions">
        <ActionRow
          icon="plus"
          title="Добавить устройство"
          subtitle={"До " + data.vpn.deviceLimit + " устройств"}
          onClick={() => go("add-devices")}
        />
        <ActionRow
          icon="refresh"
          title="Продлить VPN"
          subtitle="Ссылка останется прежней"
          onClick={() => go("renew")}
        />
      </div>
    </>
  );
}

export function PlanScreen({
  mode,
  plans,
  selected,
  setSelected,
  onSubmit,
}: {
  mode: "buy" | "renew";
  plans: Plan[];
  selected: Plan;
  setSelected: Dispatch<SetStateAction<Plan>>;
  onSubmit: () => void;
}) {
  const isRenew = mode === "renew";
  return (
    <>
      <Intro
        compact
        title={isRenew ? "Продлить VPN" : "Купить VPN"}
        subtitle={
          isRenew
            ? "Выберите срок продления. Ваш VPN и ссылка подключения останутся прежними."
            : "Первое подключение AIDAHAR"
        }
      />
      <div className="block-gap-26" />

      {isRenew ? (
        <Notice
          tone="success"
          icon="shield"
          title="Текущий VPN"
          text="При продлении ссылка не изменится"
        />
      ) : (
        <div className="purchase-explainer">
          <strong>Один VPN — одна постоянная ссылка</strong>
          <p>После покупки вы сможете продлевать этот же VPN. Ссылка подключения не будет меняться.</p>
        </div>
      )}

      <div className="block-gap-20" />
      <div className="plan-list">
        {plans.map((plan) => (
          <button
            type="button"
            className={selected.id === plan.id ? "plan-row is-selected pressable" : "plan-row pressable"}
            key={plan.id}
            onClick={() => setSelected(plan)}
          >
            <strong>{plan.label}</strong>
            <span>{plan.price} ₽</span>
          </button>
        ))}
      </div>

      <div className="plan-submit">
        <PrimaryButton icon="card" onClick={onSubmit}>
          {isRenew
            ? "Продлить на " + selected.label.toLowerCase() + " · " + selected.price + " ₽"
            : "Купить на " + selected.label.toLowerCase() + " · " + selected.price + " ₽"}
        </PrimaryButton>
      </div>
    </>
  );
}

export function AddDevicesScreen({
  currentLimit,
  maxLimit,
  pricePerDeviceRub,
  quantity,
  setQuantity,
  onContinue,
}: {
  currentLimit: number;
  maxLimit: number;
  pricePerDeviceRub: number;
  quantity: number;
  setQuantity: (value: number) => void;
  onContinue: () => void;
}) {
  return (
    <>
      <Intro compact title="Добавить устройства" subtitle="Выберите, сколько дополнительных устройств добавить" />
      <div className="block-gap-24" />
      <Notice icon="plus" title="Сейчас доступно" text={"до " + currentLimit + " устройств"} />
      <div className="block-gap-18" />
      <div className="device-options">
        {Array.from(
          { length: Math.max(0, Math.min(4, maxLimit - currentLimit)) },
          (_, index) => index + 1,
        ).map((value) => (
          <button
            type="button"
            key={value}
            className={quantity === value ? "device-option is-selected pressable" : "device-option pressable"}
            onClick={() => setQuantity(value)}
          >
            <span className="radio-dot" />
            <strong>{value} {value === 1 ? "устройство" : "устройства"}</strong>
            <span className="grow" />
            <small>{pricePerDeviceRub * value} ₽</small>
          </button>
        ))}
      </div>
      <div className="block-gap-18" />
      <div className="total-card">
        <span>Итого</span>
        <strong>{pricePerDeviceRub * quantity} ₽</strong>
      </div>
      <div className="block-gap-18" />
      <PrimaryButton icon="plus" onClick={onContinue}>Перейти к оплате</PrimaryButton>
    </>
  );
}

export function DevicePaymentScreen({
  quantity,
  pricePerDeviceRub,
  onPay,
}: {
  quantity: number;
  pricePerDeviceRub: number;
  onPay: () => void;
}) {
  return (
    <>
      <Intro compact title="Оплата устройств" subtitle="Количество и сумма будут переданы в оплату" />
      <div className="block-gap-24" />
      <div className="payment-card">
        <small>Дополнительные устройства</small>
        <strong>{quantity} {quantity === 1 ? "устройство" : "устройства"}</strong>
        <p>К оплате: {pricePerDeviceRub * quantity} ₽. Срок и ссылка VPN не изменятся.</p>
      </div>
      <div className="block-gap-20" />
      <PrimaryButton icon="card" onClick={onPay}>Оплатить</PrimaryButton>
    </>
  );
}

export function ProfileScreen({
  data,
  signedIn,
  onLogin,
  onLogout,
  go,
  loginError = false,
}: {
  data: AppSnapshot;
  signedIn: boolean;
  onLogin: () => void;
  onLogout: () => void;
  go: Go;
  loginError?: boolean;
}) {
  if (!signedIn) {
    return (
      <>
        <Intro title="Войдите в AIDAHAR" subtitle={"Один аккаунт для бота, сайта\nи Telegram Mini App"} />
        <div className="block-gap-32" />
        <div className="login-card">
          <span className="telegram-circle"><Icon name="telegram" size={32} /></span>
          <strong>Вход через Telegram</strong>
          <p>После входа здесь будут доступны<br />ваш VPN, подписка и история.</p>
          <PrimaryButton icon="telegram" onClick={onLogin}>Войти через Telegram</PrimaryButton>
        </div>
        <div className="block-gap-24" />
        {loginError ? (
          <Notice
            tone="warning"
            icon="info"
            title="Вход не завершён"
            text="Попробуйте войти через Telegram ещё раз"
          />
        ) : (
          <Notice icon="shield" title="Без пароля" text="Telegram подтверждает ваш аккаунт" />
        )}
      </>
    );
  }

  return (
    <>
      <Intro title="Профиль" subtitle="Аккаунт и настройки сервиса" />
      <div className="block-gap-28" />
      <div className="profile-card">
        <span className="avatar"><Icon name="user" size={28} /></span>
        <div>
          <strong>{data.account.firstName}</strong>
          <small>{data.account.username ?? "Telegram"}</small>
        </div>
      </div>
      <div className="block-gap-20" />
      <section className="status-card profile-info">
        <InfoRow icon="user" label="Telegram">
          {data.account.username ?? "Подключён"}
        </InfoRow>
        <InfoRow icon="shield" label="Аккаунт" last>
          <span className={data.account.isBlocked ? undefined : "success-text"}>
            {data.account.isBlocked ? "Заблокирован" : "Активен"}
          </span>
        </InfoRow>
      </section>
      <div className="block-gap-20" />
      <div className="stack-actions">
        <SecondaryButton icon="chat" onClick={() => go("support")}>Поддержка</SecondaryButton>
        <SecondaryButton icon="logout" danger onClick={onLogout}>Выйти из аккаунта</SecondaryButton>
      </div>
    </>
  );
}

export function MoreScreen({
  signedIn,
  go,
}: {
  signedIn: boolean;
  go: Go;
}) {
  return (
    <>
      <Intro title="Ещё" subtitle="Дополнительные возможности" />
      <div className="block-gap-28" />
      <div className="menu-card">
        <MenuRow icon="book" title="Инструкция" subtitle="Подключение на всех устройствах" onClick={() => go("instruction")} />
        <MenuRow icon="gift" title="Промокод" subtitle="Активировать бонус" onClick={() => go("promo")} />
        {signedIn && (
          <MenuRow icon="users" title="Реферальная программа" subtitle="Пригласить друга" onClick={() => go("referral")} />
        )}
        <MenuRow icon="chat" title="Поддержка" subtitle="Написать нам" onClick={() => go("support")} />
        <MenuRow icon="info" title="О сервисе" subtitle="AIDAHAR VPN" onClick={() => go("about")} />
      </div>
    </>
  );
}

export function InstructionScreen({
  go,
  instructionUrl,
}: {
  go: Go;
  instructionUrl?: string | null;
}) {
  const openInstruction = () => {
    if (instructionUrl) {
      window.open(instructionUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <>
      <Intro compact title="Инструкция" subtitle="Подключение за несколько минут" />
      <div className="block-gap-24" />
      <div className="menu-card">
        <MenuRow
          icon="book"
          title="1. Установите Happ"
          subtitle="На своё устройство"
          onClick={instructionUrl ? openInstruction : () => undefined}
        />
        <MenuRow icon="key" title="2. Добавьте подписку" subtitle="Используйте ссылку из «Мой VPN»" onClick={() => go("vpn")} />
        <MenuRow icon="shield" title="3. Включите VPN" subtitle="Выберите AIDAHAR и подключитесь" onClick={() => go("vpn")} />
      </div>
      <div className="block-gap-18" />
      <SecondaryButton icon="key" onClick={() => go("vpn")}>Перейти в Мой VPN</SecondaryButton>
    </>
  );
}

export function PromoScreen({
  onApply,
}: {
  onApply: (code: string) => void | Promise<void>;
}) {
  const [code, setCode] = useState("");

  return (
    <>
      <Intro compact title="Промокод" subtitle="Активируйте бонус" />
      <div className="block-gap-24" />
      <div className="form-card">
        <label htmlFor="promo">Введите промокод</label>
        <input
          id="promo"
          placeholder="AIDAHAR"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
        />
      </div>
      <div className="block-gap-16" />
      <PrimaryButton
        icon="gift"
        onClick={() => {
          const value = code.trim();
          if (value) void onApply(value);
        }}
      >
        Применить промокод
      </PrimaryButton>
    </>
  );
}

export function ReferralScreen({
  onCopy,
  signupBonusDays = 3,
  paidBonusDays = 10,
}: {
  onCopy: () => void;
  signupBonusDays?: number;
  paidBonusDays?: number;
}) {
  return (
    <>
      <Intro compact title="Реферальная программа" subtitle="Приглашайте друзей и получайте дни" />
      <div className="block-gap-24" />
      <Notice icon="users" title="Запуск по ссылке" text={"+" + signupBonusDays + " дня"} />
      <div className="block-gap-10" />
      <Notice icon="users" title="Первая оплата" text={"+" + paidBonusDays + " дней"} />
      <div className="block-gap-16" />
      <PrimaryButton icon="key" onClick={onCopy}>Скопировать реферальную ссылку</PrimaryButton>
    </>
  );
}

export function SupportScreen({
  go,
  supportUrl,
}: {
  go: Go;
  supportUrl?: string | null;
}) {
  const openTelegram = () =>
    window.open(
      supportUrl || "https://t.me/AIDAHAR_VPN_bot",
      "_blank",
      "noopener,noreferrer",
    );

  return (
    <>
      <Intro compact title="Поддержка" subtitle="Поможем с подключением" />
      <div className="block-gap-24" />
      <div className="menu-card">
        <MenuRow icon="chat" title="Написать в Telegram" subtitle="Основной канал поддержки" onClick={openTelegram} />
        <MenuRow icon="book" title="Инструкция" subtitle="Пошаговое подключение" onClick={() => go("instruction")} />
        <MenuRow icon="key" title="Мой VPN" subtitle="Ссылка и устройства" onClick={() => go("vpn")} />
      </div>
    </>
  );
}

export function AboutScreen({
  data,
  aboutText,
}: {
  data: AppSnapshot;
  aboutText?: string | null;
}) {
  return (
    <>
      <Intro compact title="О сервисе" subtitle="AIDAHAR VPN" />
      <div className="block-gap-24" />
      <Notice
        icon="info"
        title="AIDAHAR VPN"
        text={
          aboutText ||
          (data.vpn.regions.length
            ? data.vpn.regions.join(" + ")
            : "Доступные регионы указаны в тарифе")
        }
      />
      <div className="block-gap-14" />
      <Notice icon="shield" title="Трафик" text="Безлимитный в активной подписке" />
    </>
  );
}

export function TermsScreen({
  legal,
  onAccept,
}: {
  legal: Array<{ title: string; url: string }>;
  onAccept: () => void | Promise<void>;
}) {
  return (
    <>
      <Intro compact title="Условия сервиса" subtitle="Один раз подтвердите условия перед покупкой VPN" />
      <div className="block-gap-24" />
      <Notice
        icon="shield"
        title="Аккаунт подключён"
        text="Telegram подтвердил ваш профиль. Осталось принять условия сервиса."
      />
      <div className="block-gap-18" />
      <div className="menu-card">
        {legal.map((item) => (
          <MenuRow
            key={item.url}
            icon="book"
            title={item.title}
            subtitle="Открыть документ"
            onClick={() => window.open(item.url, "_blank", "noopener,noreferrer")}
          />
        ))}
      </div>
      <div className="block-gap-18" />
      <PrimaryButton icon="shield" onClick={() => void onAccept()}>
        Принимаю условия
      </PrimaryButton>
    </>
  );
}

export function SuccessScreen({
  kind,
  onContinue,
}: {
  kind: "purchase" | "renew";
  onContinue: () => void;
}) {
  return (
    <>
      <Intro
        compact
        title={kind === "purchase" ? "VPN создан" : "VPN продлён"}
        subtitle={kind === "purchase" ? "Покупка прошла успешно" : "Подписка успешно обновлена"}
      />
      <div className="block-gap-24" />
      <Notice
        tone="success"
        icon="shield"
        title="Готово"
        text={kind === "purchase" ? "Ваша постоянная ссылка VPN создана" : "Ссылка VPN осталась прежней"}
      />
      <div className="block-gap-18" />
      <PrimaryButton icon="key" onClick={onContinue}>
        {kind === "purchase" ? "Перейти в Мой VPN" : "Открыть мой VPN"}
      </PrimaryButton>
    </>
  );
}
